/**
 * SignupForm Component
 *
 * A user registration form component that collects username (email) and handles user registration.
 * It ensures basic validation using a Zod schema for secure form submission.
 *
 * Features:
 * - Username (email) field with validation
 * - Form validation using Zod and React Hook Form
 * - Terms of Service and Privacy Policy acknowledgement checkbox
 * - Loading state handling during async submission
 *
 */

import { useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getSignupFormValidationSchema, signupFormDefaultValue, signupFormType } from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Captcha, ErrorAlert, useCaptcha } from '@/components/core';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Button } from '@/components/ui-kit/button';
import { useNavigate } from 'react-router-dom';
import { useSignUp } from '@/api-client/use-signup';
import { GoogleLoginButton } from '../google-login-button';
import { MicrosoftLoginButton } from '../microsoft-login-button';
import { generateOrganizationName } from '@/utils/common';

export const SignupForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const googleSiteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY || '';
  const captchaEnabled = googleSiteKey !== '';

  const { execute, captcha, reset: resetCaptcha } = useCaptcha({
    siteKey: googleSiteKey,
    type: "v3",
  });

  const form = useForm({
    defaultValues: signupFormDefaultValue,
    resolver: zodResolver(getSignupFormValidationSchema(t)),
    mode: 'onChange',
  });

  const { isPending, mutateAsync: signUp, isError, error } = useSignUp();
  const email = useWatch({ control: form.control, name: 'email' });
  const isValidEmail = !!(email && !form.formState.errors.email);

  // Keep the failure banner up only while the email that failed is still in the
  // field. Editing the email hides it (and prompts a fresh captcha + submit).
  const failedEmail = useRef('');
  const showError = isError && email === failedEmail.current;

  const showCaptcha = captchaEnabled;

  const isSubmitDisabled = isPending || !isValidEmail;

  const errorMessage = (() => {
    if (!error) return '';
    const apiError = (error as unknown as Record<string, unknown>)?.error;
    if (apiError && typeof apiError === 'object') {
      const errors = (apiError as Record<string, unknown>)?.errors;
      if (errors && typeof errors === 'object') {
        return Object.values(errors)[0] as string;
      }
    }
    return error.message || 'An unexpected error occurred.';
  })();

  const onSubmitHandler = async ({ email }: signupFormType) => {
    try {
      const captchaCode = captchaEnabled ? (await execute()) || '' : '';
      await signUp({
        email: email,
        captchaCode,
        mailPurpose: '',
        isSsoSignup: false,
        provider: '',
        externalUserId: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        clientId: '',
        redirectUri: '',
        createOrganizationDuringSignup: true,
        organizationName: generateOrganizationName(email),
        organizationDescription: '',
        attributes: {

        }
      });

      navigate(`/signup-email-sent?email=${email}`);
    } catch {
      failedEmail.current = email;
      resetCaptcha();
    }
  };

  return (
    <div className="w-full">
      <ErrorAlert isError={showError} title={'Sign Up Failed'} message={errorMessage} persist />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmitHandler)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input autoComplete="username" placeholder={'Enter your email.'} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {captchaEnabled && showCaptcha && (
            <div className="my-4">
              <Captcha {...captcha} theme="light" size="normal" />
            </div>
          )}

          <div className="text-center text-sm text-medium-emphasis my-6">
            By continuing, you agree to the
            <a className="text-selise-blue-500 underline" href={'https://legal-templates.com/en'}>
              Terms of Services
            </a>{' '}
            and acknowledge that you have read our{' '}
            <a
              className="text-selise-blue-500 underline"
              href={'https://legal-templates.com/en/privacy-policy'}
            >
              Privacy Policy
            </a>
            .
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitDisabled}>
            Continue
          </Button>
        </form>
      </Form>

      <div className="w-full flex flex-col gap-6 mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-muted" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">OR</span>
          </div>
        </div>
        <div className="flex justify-center gap-4">
          <GoogleLoginButton iconOnly />
          <MicrosoftLoginButton iconOnly />
        </div>
      </div>
    </div>
  );
};
